import { Info } from "lucide-react";
import { useTranslation } from "react-i18next";

import { AgentInfoTag } from "@/components/agent-badge";
import { AgentDropdown } from "@/components/agent-dropdown";
import { TaskStatusAdvanceButton } from "@/components/kanban/TaskStatusAdvanceButton";
import { TaskStatusButton, type TaskStatusButtonDensity } from "@/components/kanban/TaskStatusButton";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { Agent } from "@/hooks/use-agents";
import type { TaskStatus } from "@/lib/project-types";
import { cn, type AccessTokenGetter } from "@/lib/utils";

export type ConversationHeaderControlDensity = "full" | "compact" | "icon";

interface SharedTaskBadgeProps {
  className?: string;
}

/** Compact shared-task context, with its explanation available on the info icon. */
export function SharedTaskBadge({ className }: SharedTaskBadgeProps) {
  const { t } = useTranslation();
  const explanation = t("header.sharedTaskConversationTooltip");

  return (
    <Badge
      asChild
      variant="secondary"
      className={cn(
        "shrink-0 gap-1 whitespace-nowrap border-border px-1.5 py-0.5 font-medium",
        className,
      )}
    >
      <span data-testid="shared-task-conversation-badge">
        <span>{t("header.sharedTaskConversation")}</span>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="-mr-0.5 inline-flex size-4 shrink-0 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-background/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                aria-label={explanation}
              >
                <Info className="size-3" aria-hidden="true" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-64 text-center">
              {explanation}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </span>
    </Badge>
  );
}

export interface ConversationHeaderControlsProps {
  agents: Agent[];
  agentsLoading: boolean;
  agentsError: string | null;
  currentAgentId: string | null;
  onAgentChange: (agentId: string) => void;
  showAgentSwitcher: boolean;
  canManageSession: boolean;
  selectionLocked: boolean;
  /** Show identity without offering an agent mutation control. */
  showReadOnlyAgentInfo?: boolean;
  getToken: AccessTokenGetter;
  isAdminUser: boolean;
  isSharedTaskConversation: boolean;
  taskStatus?: TaskStatus;
  onTaskStatusChange?: (status: TaskStatus) => Promise<boolean> | boolean;
  density: ConversationHeaderControlDensity;
  /** Full-width controls are easier to use when rendered inside overflow. */
  inOverflow?: boolean;
  className?: string;
}

/**
 * The selected-agent, shared-task, and task-status controls used by the
 * conversation header. The same controls can move into the overflow menu
 * without changing their data or server-facing behavior.
 */
export function ConversationHeaderControls({
  agents,
  agentsLoading,
  agentsError,
  currentAgentId,
  onAgentChange,
  showAgentSwitcher,
  canManageSession,
  selectionLocked,
  showReadOnlyAgentInfo = false,
  getToken,
  isAdminUser,
  isSharedTaskConversation,
  taskStatus,
  onTaskStatusChange,
  density,
  inOverflow = false,
  className,
}: ConversationHeaderControlsProps) {
  const currentAgent = agents.find((agent) => agent.id === currentAgentId);
  const canShowAgentSelector = showAgentSwitcher && canManageSession && !selectionLocked;
  const canShowAgentInfo = currentAgentId !== null && (
    showReadOnlyAgentInfo
    || (showAgentSwitcher && canManageSession && selectionLocked)
  );
  const canShowAgentControl = canShowAgentSelector || canShowAgentInfo;
  const statusDensity: TaskStatusButtonDensity = inOverflow ? "full" : density;
  const hasTaskStatusControl = taskStatus !== undefined && onTaskStatusChange !== undefined;

  if (!canShowAgentControl && !isSharedTaskConversation && !hasTaskStatusControl) return null;

  return (
    <div
      className={cn(
        "flex min-w-0 items-center justify-end gap-2",
        inOverflow && "flex-col items-stretch justify-start gap-2 px-1 py-1",
        className,
      )}
      data-testid={inOverflow ? "conversation-header-overflow-controls" : "conversation-header-controls"}
    >
      {canShowAgentControl && (
        canShowAgentInfo ? (
          <AgentInfoTag
            agentId={currentAgentId}
            agent={currentAgent}
            getToken={getToken}
            compact={!inOverflow && density !== "full"}
            className={cn(inOverflow && "self-start")}
          />
        ) : (
          <AgentDropdown
            className={cn("min-w-0", inOverflow && "w-full")}
            agents={agents}
            currentAgentId={currentAgentId}
            loading={agentsLoading}
            error={agentsError}
            getToken={getToken}
            onSelect={onAgentChange}
            isAdminUser={isAdminUser}
            fullWidth={inOverflow}
            triggerDensity={density === "full" || inOverflow ? "full" : "compact"}
          />
        )
      )}
      {isSharedTaskConversation && <SharedTaskBadge className={cn(inOverflow && "self-start")} />}
      {hasTaskStatusControl && (
        <>
          <TaskStatusButton
            status={taskStatus}
            onStatusChange={onTaskStatusChange}
            density={statusDensity}
          />
          {!inOverflow && (
            <TaskStatusAdvanceButton
              status={taskStatus}
              onStatusChange={onTaskStatusChange}
              density={density}
            />
          )}
        </>
      )}
    </div>
  );
}
